import { CommonModule, isPlatformBrowser } from '@angular/common';
import type { AfterViewInit, ElementRef } from '@angular/core';
import {
	Component,
	computed,
	contentChildren,
	CUSTOM_ELEMENTS_SCHEMA,
	HostListener,
	inject,
	input,
	model,
	PLATFORM_ID,
	signal,
	viewChild,
} from '@angular/core';

import { cn } from '@valk-nx/core/lib/core';
import { debounceFn } from '@valk-nx/helpers/lib/debounce/debounce.helper';
import { DocumentHelper } from '@valk-nx/helpers/lib/document/document.helper';

import { TabMenuItemDirective } from './tab-menu.directive';

@Component({
	selector: `vp-tab-menu`,
	templateUrl: './tab-menu.html',
	imports: [CommonModule],
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class TabMenuComponent implements AfterViewInit {
	private readonly platformId = inject(PLATFORM_ID);
	protected readonly cn = cn;
	spacing = input<'compact' | 'comfortable'>();
	tabMenuId = input('tab-menu');

	activeTab = model(0);

	color = input<'white' | 'accent'>('white');
	stretchTabs = input(false);

	useButtonVariant = input(false);
	// Is only being used if useButtonVariant is true
	numberOfColumns = input<2 | 3 | 4>(3);
	// Is only being used if useButtonVariant is true
	hideTabs = input(false);

	// NOTE: Implemented as quick solution for BT tab menu items not getting translated on language switch.
	// Will be investigated further and properly fixed in [VPL-40149]
	refreshToken = input<unknown>(null);

	tabMenuItems = contentChildren(TabMenuItemDirective);
	tabMenuContainer = viewChild<ElementRef<HTMLElement>>('tabMenuContainer');

	// Is only being used if useButtonVariant is true
	numberOfColumnsTab = computed<string>(() => {
		switch (this.numberOfColumns()) {
			case 2:
				return 'md:grid-cols-2';
			case 3:
				return 'md:grid-cols-3';
			case 4:
				return 'md:grid-cols-4';
		}
	});

	tabNames = computed<string[]>(
		() => this.tabMenuItems()?.map((item) => item.name) ?? [],
	);

	backgroundColor = computed(() => {
		switch (this.color()) {
			case 'white':
				return 'bg-white';
			case 'accent':
				return 'bg-accent-25';
			default:
				return '';
		}
	});

	startGradient = computed(() => {
		switch (this.color()) {
			case 'white':
				return 'from-white';
			case 'accent':
				return 'from-accent-25';
			default:
				return '';
		}
	});

	endGradient = computed(() => {
		switch (this.color()) {
			case 'white':
				return 'to-white';
			case 'accent':
				return 'to-accent-25';
			default:
				return '';
		}
	});

	containerGapClass = computed(() => {
		switch (this.spacing()) {
			case 'compact':
				return 'gap-1.5 md:gap-2 lg:gap-3';
			case 'comfortable':
				return 'gap-6 md:gap-8 lg:gap-10';
			default:
				return 'gap-4 md:gap-6 lg:gap-8';
		}
	});

	setActiveTab(tabIndex: number) {
		this.activeTab.set(tabIndex);
	}

	readonly scrollLeft = signal(0);
	readonly scrollWidth = signal(0);
	readonly clientWidth = signal(0);

	isAtStart = computed<boolean>(() => {
		return this.scrollLeft() <= 0;
	});

	isAtEnd = computed<boolean>(() => {
		return this.scrollLeft() >= this.scrollWidth() - this.clientWidth() - 1;
	});

	canScroll = computed<boolean>(() => {
		return this.scrollWidth() > this.clientWidth();
	});

	private readonly debouncedUpdateScrollVariables = debounceFn(
		() => this.updateScrollVariables(),
		100,
	);

	ngAfterViewInit() {
		if (this.tabMenuContainer()) {
			if (isPlatformBrowser(this.platformId)) {
				// Determine the scroll variables after the fonts have been loaded.
				// This is to prevent the component to determine canScroll before the right fonts have loaded, which might result in an inaccurate value.
				DocumentHelper.whenFontsAreLoaded().then(() =>
					this.updateScrollVariables(),
				);
			} else {
				this.updateScrollVariables();
			}
		}
	}

	onWheel(event: WheelEvent) {
		const element = event.currentTarget as HTMLElement;
		if (
			element &&
			this.canScroll() &&
			Math.abs(event.deltaY) > Math.abs(event.deltaX)
		) {
			event.preventDefault();
			element.scrollLeft += event.deltaY;
			this.updateScrollPosition(element);
		}
	}

	onScroll(event: Event) {
		const element = event.currentTarget as HTMLElement;
		this.updateScrollPosition(element);
	}

	onTabKeydown(event: KeyboardEvent, currentIndex: number) {
		switch (event.key) {
			case 'ArrowLeft': {
				event.preventDefault();
				this.goToPreviousTab(currentIndex, true);
				break;
			}
			case 'ArrowRight': {
				event.preventDefault();
				this.goToNextTab(currentIndex, true);
				break;
			}
			default:
				break;
		}
	}

	goToPreviousTab(currentIndex: number, focusTab: boolean) {
		const tabCount = this.tabMenuItems().length;
		const prevIndex = currentIndex > 0 ? currentIndex - 1 : tabCount - 1;
		this.setActiveTab(prevIndex);
		if (focusTab) {
			this.focusTab(prevIndex);
		}
	}

	goToNextTab(currentIndex: number, focusTab: boolean) {
		const tabCount = this.tabMenuItems().length;
		const nextIndex = currentIndex < tabCount - 1 ? currentIndex + 1 : 0;
		this.setActiveTab(nextIndex);
		if (focusTab) {
			this.focusTab(nextIndex);
		}
	}

	protected focusTab(index: number) {
		setTimeout(() => {
			const tabElement = document.querySelector(
				`[data-name="${this.tabMenuItems()[index]?.name}"]`,
			);
			if (tabElement) {
				(tabElement as HTMLElement).focus();
				tabElement.scrollIntoView({
					behavior: 'smooth',
					block: 'nearest',
					inline: 'center',
				});
			}
		}, 0);
	}

	updateScrollPosition(element: HTMLElement) {
		this.scrollLeft.set(element.scrollLeft);
	}

	@HostListener('window:resize')
	onResize() {
		this.debouncedUpdateScrollVariables();
	}

	private updateScrollVariables() {
		const element = this.tabMenuContainer().nativeElement;
		this.scrollLeft.set(element.scrollLeft);
		this.scrollWidth.set(element.scrollWidth);
		this.clientWidth.set(element.clientWidth);
	}
}
